import { Injectable } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class PdfmakeService {
  constructor(private currencyPipe: CurrencyPipe, private datePipe: DatePipe) {}

  /**
   * Genera un PDF de liquidación usando pdfmake
   */
  async generateLiquidacionPDF(data: any) {
    const docDefinition = await this.getDocDefinition(data);

    let watermarkImage = '';
    try {
      watermarkImage = await this.getBase64Image('assets/img/helipagos-watermark.png');
    } catch (error) {
      console.error('Error loading watermark:', error);
    }

    if (watermarkImage) {
      docDefinition.background = (_currentPage: number, pageSize: any) => ({
        image: watermarkImage,
        width: 280,
        opacity: 0.1,
        absolutePosition: {
          x: (pageSize.width - 280) / 2,
          y: (pageSize.height - 200) / 2,
        },
      });
    }

    pdfMake.createPdf(docDefinition).download(`liquidacion-nro-${data.id}-pdf.pdf`);
  }

  /**
   * Obtiene la definición del documento PDF
   */
  private async getDocDefinition(data: any): Promise<any> {
    const content = [
      {
        table: {
          widths: [150, '*'],
          body: [
            [
              {
                image: await this.getBase64Image('assets/img/helipagos-color.png'),
                width: 150,
              },
              {
                text: 'Liquidación Nro. ' + data.id,
                style: 'header',
                alignment: 'right',
                margin: [10, 0, 0, 0],
              },
            ],
          ],
        },
        layout: 'noBorders',
      },
      this.getClientDetails(data),
      this.getDepositInfo(data.importe_liquidado),
    ];

    // Resumen
    const resumen = this.buildResumen(data);
    if (resumen.length > 0) {
      content.push(
        this.getSummarySection('Resumen', 15),
        this.getFixedSummaryAmounts(resumen)
      );
    }

    // Cuentas
    const cuentas = this.buildCuentas(data);
    if (cuentas.length > 0) {
      content.push(this.getFixedSummaryAmounts(cuentas));
    }

    // Impuestos
    const impuestos = this.buildImpuestos(data);
    if (impuestos.length > 0) {
      content.push(
        this.getSummarySection('Impuestos', 2),
        this.getFixedSummaryAmounts(impuestos)
      );
    }

    // Otras deducciones
    const costosVariables = this.buildCostosVariables(data);
    if (costosVariables.length > 0) {
      content.push(
        this.getSummarySection('Otras deducciones', 2),
        this.getFixedSummaryAmounts(costosVariables)
      );
    }

    // Retenciones
    const retenciones = this.buildRetenciones(data);
    if (retenciones.length > 0) {
      content.push(
        this.getSummarySection('Retenciones', 2),
        this.getFixedSummaryAmounts(retenciones)
      );
    }

    // Detalles
    const rendiciones = data.rendicion_solicitud_pago || [];
    if (rendiciones.length > 0) {
      content.push(
        this.getSummarySection('Detalles', 10),
        this.getHorizontalLine(),
        this.getEightColumnTable(rendiciones, data.importe_liquidado)
      );
    }

    return {
      content: content,
      styles: this.getStyles(),
      pageMargins: [25, 25, 25, 25],
    };
  }

  /**
   * Construye el array de resumen
   */
  private buildResumen(data: any): any[] {
    const resumen: any[] = [];

    // Importe percibido
    resumen.push({
      concepto: 'Importe percibido',
      valor: this.formatCurrency(data.importe_total / 100),
    });

    // Comisión Helipagos
    resumen.push({
      concepto: 'Comisión Helipagos',
      valor: '- ' + this.formatCurrency(data.importe_comision / 100),
    });

    // Bonificación (si existe)
    if (data.importe_bonificacion_comision != null) {
      resumen.push({
        concepto: 'Bonificación',
        valor: this.formatCurrency(data.importe_bonificacion_comision / 100),
      });
    }

    // Gastos
    resumen.push({
      concepto: 'Gastos',
      valor: '- ' + this.formatCurrency((data.importe_gasto || data.importe_gasto_comision || 0) / 100),
    });

    // Contracargos
    resumen.push({
      concepto: 'Contracargos',
      valor: '- ' + this.formatCurrency((data.importe_contracargo || 0) / 100),
    });

    // Devoluciones (siempre $0)
    resumen.push({
      concepto: 'Devoluciones',
      valor: '- $ 0.00 (0)',
    });

    // Costo Financiero (si != null y != 0)
    if (data.importe_costo_financiero != null && data.importe_costo_financiero !== 0) {
      resumen.push({
        concepto: 'Costo Financiero',
        valor: '- ' + this.formatCurrency(data.importe_costo_financiero / 100),
      });
    }

    // Cantidad de operaciones
    const cantOperaciones = (data.rendicion_solicitud_pago || []).reduce(
      (sum: number, r: any) => sum + (r.cantidad_solicitud ?? r.cantidad ?? 0),
      0
    );
    resumen.push({
      concepto: 'Cantidad de operaciones',
      valor: cantOperaciones.toString(),
    });

    return resumen;
  }

  /**
   * Construye el array de cuentas
   */
  private buildCuentas(data: any): any[] {
    const cuentas: any[] = [];

    if (data.importe_neto_cuenta_a != null) {
      cuentas.push({
        concepto: 'Importe cuenta A',
        valor: this.formatCurrency(data.importe_neto_cuenta_a / 100),
      });
    }

    if (data.importe_neto_cuenta_b != null) {
      cuentas.push({
        concepto: 'Importe cuenta B',
        valor: this.formatCurrency(data.importe_neto_cuenta_b / 100),
      });
    }

    return cuentas;
  }

  /**
   * Construye el array de impuestos
   */
  private buildImpuestos(data: any): any[] {
    const impuestos: any[] = [];

    if (data.importe_iva != null) {
      impuestos.push({
        concepto: 'IVA 21,00%',
        valor: '- ' + this.formatCurrency(data.importe_iva / 100),
        tipo: 'Impuestos',
      });
    }

    if (data.importe_iibb_misiones != null) {
      impuestos.push({
        concepto: 'IIBB Misiones 3,31%',
        valor: '- ' + this.formatCurrency(data.importe_iibb_misiones / 100),
        tipo: 'Percepciones',
      });
    }

    return impuestos;
  }

  /**
   * Construye el array de costos variables
   */
  private buildCostosVariables(data: any): any[] {
    if (!data.costosVariables || !Array.isArray(data.costosVariables)) {
      return [];
    }

    return data.costosVariables.map((costo: any) => ({
      concepto: costo.descripcion,
      valor: this.formatCurrency(costo.importe / 100),
    }));
  }

  /**
   * Construye el array de retenciones
   */
  private buildRetenciones(data: any): any[] {
    const retenciones: any[] = [];

    if (data.importe_sirtac != null) {
      retenciones.push({
        concepto: 'SIRTAC 0%',
        valor: '- ' + this.formatCurrency(data.importe_sirtac / 100),
      });
    }

    if (data.importe_ret_iva != null) {
      retenciones.push({
        concepto: 'Ret. IVA',
        valor: '- ' + this.formatCurrency(data.importe_ret_iva / 100),
      });
    }

    if (data.importe_ret_ganancia != null) {
      retenciones.push({
        concepto: 'Ret. ganancias',
        valor: '- ' + this.formatCurrency(data.importe_ret_ganancia / 100),
      });
    }

    return retenciones;
  }

  /**
   * Obtiene los detalles del cliente
   */
  private getClientDetails(data: any): any {
    const clientId = data.cliente?.id ? `${data.cliente.id} - ` : '';
    const clientName = data.cliente?.nombre_legal || data.cliente || '';
    const textToShow = `${clientId}${clientName}`;

    return {
      table: {
        widths: [350, '*'],
        body: [
          [
            {
              text: textToShow,
              style: 'subheader',
              color: '#000000',
              margin: [0, 15, 0, 0],
            },
            this.getPeriodDetailsTable(data),
          ],
        ],
      },
      layout: 'noBorders',
    };
  }

  /**
   * Obtiene la tabla de detalles del período
   */
  private getPeriodDetailsTable(data: any): any {
    const fechaStart = data.fecha_start || data.fecha_start_period;
    const fechaEnd = data.fecha_end || data.fecha_end_period;
    const fechaEmision = data.fecha || data.created_at;
    const fechaDeposito = data.fecha_transferencia || data.fecha || data.created_at;

    return {
      table: {
        widths: ['*', 300],
        body: [
          [
            {
              text: 'Período',
              style: 'subheader',
              margin: [0, 15, 0, 0],
              fontSize: 10,
            },
            {
              text: `${this.formatDate(fechaStart)} a ${this.formatDate(fechaEnd)}`,
              style: 'subheader',
              color: '#000000',
              margin: [0, 15, 0, 0],
              fontSize: 10,
            },
          ],
          this.getPeriodDetailRow('Emisión', this.formatDate(fechaEmision)),
          this.getPeriodDetailRow('Depósito', this.formatDate(fechaDeposito)),
        ],
      },
      layout: 'noBorders',
    };
  }

  /**
   * Obtiene una fila de detalles del período
   */
  private getPeriodDetailRow(label: string, value: string): any {
    return [
      {
        text: label,
        style: 'subheader',
        margin: [0, 2, 0, 0],
        fontSize: 10,
      },
      {
        text: value,
        style: 'subheader',
        color: '#000000',
        margin: [0, 2, 0, 0],
        fontSize: 10,
      },
    ];
  }

  /**
   * Obtiene la sección de información de depósito
   */
  private getDepositInfo(amount: number): any {
    return {
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'Importe depositado',
              alignment: 'center',
              fontSize: 11,
              bold: true,
              margin: [0, 10, 0, 5],
            },
          ],
          [
            {
              text: this.formatCurrency(amount / 100),
              alignment: 'center',
              fontSize: 32,
              bold: true,
              color: '#002DBC',
              margin: [0, 5, 0, 10],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        fillColor: () => '#f5f5f5',
      },
      margin: [0, 15, 0, 15],
    };
  }

  /**
   * Obtiene la sección de resumen
   */
  private getSummarySection(title: string, marginTop: number): any {
    return {
      text: title,
      style: 'sectionHeader',
      margin: [0, marginTop, 0, 3],
    };
  }

  /**
   * Obtiene la tabla de montos resumidos
   */
  private getFixedSummaryAmounts(data: any): any {
    const body = data.map((item: any) => [
      {
        text: item.concepto || '',
        style: 'tableText',
        alignment: 'left',
      },
      {
        text: item.valor || '',
        style: 'tableText',
        alignment: 'right',
        noWrap: true,
      },
    ]);

    return {
      table: {
        widths: ['*', 150],
        body: body,
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0 : 0.5),
        vLineWidth: () => 0,
        hLineColor: () => '#cccccc',
      },
      margin: [0, 0, 0, 10],
    };
  }

  /**
   * Obtiene una línea horizontal
   */
  private getHorizontalLine(): any {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 500,
          y2: 0,
          lineWidth: 1,
          lineColor: '#cccccc',
        },
      ],
      margin: [0, 10, 0, 10],
    };
  }

  /**
   * Obtiene la tabla de 8 columnas para detalles
   */
  private getEightColumnTable(data: any, totalAmount: number): any {
    const headers = ['Rendición Nº', 'Cant.', 'Bruto', 'Comisión + IVA', 'Gastos + IVA', 'Contracargo', 'Fecha', 'Neto'];

    const body = [
      headers.map((h: string) => ({
        text: h,
        style: 'tableHeader',
        alignment: h === 'Rendición Nº' ? 'left' : 'right',
      })),
    ];

    data.forEach((item: any) => {
      body.push([
        { text: 'Rendición Nº ' + (item.id || ''), style: 'tableText', alignment: 'left' } as any,
        { text: (item.cantidad_solicitud ?? item.cantidad ?? '').toString(), style: 'tableText', alignment: 'right' } as any,
        { text: this.formatCurrency((item.importe || item.importe_total || 0) / 100), style: 'tableText', alignment: 'right', noWrap: true } as any,
        { text: this.formatCurrency((item.importe_comision || 0) / 100), style: 'tableText', alignment: 'right', noWrap: true } as any,
        { text: this.formatCurrency((item.importe_gasto || item.importe_gatos_comision || 0) / 100), style: 'tableText', alignment: 'right', noWrap: true } as any,
        { text: this.formatCurrency((item.importe_contracargo || 0) / 100), style: 'tableText', alignment: 'right', noWrap: true } as any,
        { text: this.formatDate(item.fecha_a_acreditar || item.fecha_acreditado || item.fecha || item.created_at), style: 'tableText', alignment: 'right', noWrap: true } as any,
        { text: this.formatCurrency((item.importe_liquidado || item.importe_a_acreditar || 0) / 100), style: 'tableText', alignment: 'right', noWrap: true } as any,
      ]);
    });

    // Fila de totales
    const totalBruto = data.reduce((sum: number, item: any) => sum + (item.importe || item.importe_total || 0), 0);
    const totalComision = data.reduce((sum: number, item: any) => sum + (item.importe_comision || 0), 0);
    const totalGastos = data.reduce((sum: number, item: any) => sum + (item.importe_gasto || item.importe_gatos_comision || 0), 0);
    const totalContracargo = data.reduce((sum: number, item: any) => sum + (item.importe_contracargo || 0), 0);
    const totalLiquidado = data.reduce((sum: number, item: any) => sum + (item.importe_liquidado || item.importe_a_acreditar || 0), 0);

    body.push([
      { text: 'Total', style: 'tableBold', colSpan: 2, alignment: 'left' } as any,
      { text: '', style: 'tableBold', alignment: 'left' } as any,
      { text: this.formatCurrency(totalBruto / 100), style: 'tableBold', alignment: 'right', noWrap: true } as any,
      { text: this.formatCurrency(totalComision / 100), style: 'tableBold', alignment: 'right', noWrap: true } as any,
      { text: this.formatCurrency(totalGastos / 100), style: 'tableBold', alignment: 'right', noWrap: true } as any,
      { text: this.formatCurrency(totalContracargo / 100), style: 'tableBold', alignment: 'right', noWrap: true } as any,
      { text: '', style: 'tableBold', alignment: 'center' } as any,
      { text: this.formatCurrency(totalLiquidado / 100), style: 'tableBold', alignment: 'right', noWrap: true } as any,
    ]);

    return {
      table: {
        widths: ['auto', '5%', '13%', '15%', '14%', '11%', '11%', '15%'],
        body: body,
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length ? 1 : 0.5),
        vLineWidth: () => 0,
        hLineColor: () => '#cccccc',
        paddingLeft: () => 5,
        paddingRight: () => 5,
      },
    };
  }

  /**
   * Obtiene los estilos del documento
   */
  private getStyles(): any {
    return {
      header: {
        fontSize: 18,
        bold: true,
        color: '#002DBC',
      },
      subheader: {
        fontSize: 10,
        bold: true,
        color: '#002DBC',
      },
      sectionHeader: {
        fontSize: 9,
        bold: true,
        color: '#002DBC',
      },
      tableHeader: {
        fontSize: 8,
        bold: true,
        color: '#000000',
        fillColor: '#eef0f8',
        alignment: 'center',
      },
      tableText: {
        fontSize: 9,
        color: '#333333',
      },
      tableBold: {
        fontSize: 9,
        bold: true,
        color: '#002DBC',
      },
    };
  }

  /**
   * Formatea una moneda
   */
  private formatCurrency(value: number): string {
    const num = this.currencyPipe.transform(value, 'ARS', '', '1.2-2', 'es-AR');
    return num ? '$ ' + num.trim() : '$ 0,00';
  }

  /**
   * Formatea una fecha
   */
  private formatDate(date: any): string {
    return this.datePipe.transform(date, 'dd/MM/yy') || '';
  }

  /**
   * Convierte una imagen a base64
   */
  private async getBase64Image(path: string): Promise<string> {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return '';
    }
  }
}
